import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { numero } from "../lib/admin";
import { rolarAte } from "../lib/rolarPara";
import { horaComArtigo } from "../lib/horaNoTexto";
import { GraficoDeLinhas, type SerieDoGrafico } from "./AdminAvaliacoesGraficos";
import { SeloDaMedia, SinalDaVariacao, comMaiuscula, fraseDaImportacao, fraseSolta, type Posicao } from "./AdminAvaliacoesUi";
import {
  AINDA_SEM_AVALIACAO,
  ROTULO_DA_ORIGEM,
  buscaSoDoPeriodo,
  chaveDoPedido,
  chaveDoPeriodo,
  diaBr,
  lerAvaliacoes,
  lerSeries,
  metaLegivel,
  notaLegivel,
  quandoLegivel,
  sentidoDaVariacao,
  useLeitura,
  variacaoLegivel,
  type ComparacaoDoPeriodo,
  type Granularidade,
  type Importacao,
  type LinhaDaFerramenta,
  type ListaDeAvaliacoes,
  type PedidoDePeriodo,
  type Periodo,
  type PontoDaFerramenta,
} from "../lib/avaliacoes";

/**
 * As AVALIAÇÕES de uma ferramenta, na página dela em Admin › Avaliações ›
 * Ferramentas (000326, F9; 000327; 000328): o que o time de Produtos lia no
 * detalhe da ferramenta no painel da Central Feedbacks (`feedbacks-cf`), com as
 * avaliações do hub ao lado das que vieram do app antigo.
 *
 * Três partes, e cada uma sabe de onde vem o que mostra:
 *
 *   · **os números do período** saem da `linha` que a sub-aba já leu
 *     (`av_admin_comparacao`) — sem leitura nova. Uma segunda leitura dos
 *     mesmos números seria uma segunda fotografia, e as duas podiam discordar
 *     na mesma tela;
 *   · **a evolução** é a leitura das séries (`av_admin_series`), com os baldes
 *     do período — por hora, dia, semana ou mês, conforme o tamanho dele. Ela
 *     substituiu, na 000328, a tabela "por mês" da 000327: num período de 30
 *     dias, "mês a mês" é um ponto só;
 *   · **os comentários** são a lista dos Feedbacks (`av_admin_avaliacoes`)
 *     estreitada a esta ferramenta e às avaliações COM comentário — a mesma
 *     função, e não uma segunda definição de "o que é um comentário do
 *     período". As sem comentário estão lá, e o link leva.
 *
 * ZERO conta aqui: médias, contagens, a separação da equipe, a variação contra
 * o período anterior, o dia de São Paulo e os baldes vêm do banco. A tela
 * formata, alinha a série aos baldes pela CHAVE e desenha.
 *
 * E a tela mostra a nota e o comentário, nunca quem escreveu: o banco nem
 * devolve (a regra das pesquisas).
 *
 * O período destes números é o do PAINEL, e quem o declara é a raiz da sub-aba
 * (`data-periodo-dos-dados`, da resposta da comparação); os comentários
 * declaram o deles (`data-periodo-das-avaliacoes`, da resposta da lista) e a
 * evolução, o dela (`data-periodo-do-grafico`). Entre o clique no período e as
 * respostas, cada parte continua mostrando — e dizendo — o período antigo, e
 * quem espera "pronto" sem olhar o período lê o antigo (a cicatriz da busca da
 * Central, 14/09).
 */

/** 20 por página: o pedido de Victor em 29/09 ("coloca uma paginação na lista"), com o tamanho que a prova já mede. */
const POR_PAGINA = 20;

/** O recorte do eixo, que a frase do gráfico diz com letra: o eixo mostra a unidade, a frase diz o que ela é. */
const POR_GRANULARIDADE: Record<Granularidade, string> = {
  hora: "por hora",
  dia: "por dia",
  semana: "por semana, começando na segunda",
  mes: "por mês",
};

const DE_CADA_PONTO: Record<Granularidade, string> = {
  hora: "daquela hora",
  dia: "daquele dia",
  semana: "daquela semana",
  mes: "daquele mês",
};

/**
 * As quatro linhas da evolução, e a posição de cada uma — a posição decide a
 * cor e a FORMA do marcador (`Marcador`), então ela é fixa por NOTA: a média
 * geral é sempre a primeira, e trocar o período não troca as cores.
 */
const NOTAS_DA_EVOLUCAO = [
  { id: "media_geral", rotulo: "Média geral", p: 1 },
  { id: "utilidade", rotulo: "Utilidade", p: 2 },
  { id: "facilidade", rotulo: "Facilidade", p: 3 },
  { id: "satisfacao", rotulo: "Satisfação", p: 4 },
] as const satisfies ReadonlyArray<{ id: Exclude<keyof PontoDaFerramenta, "chave" | "avaliacoes">; rotulo: string; p: Posicao }>;

/** "de 31/08/2026 a 29/09/2026", ou "em 29/09/2026" quando é um dia só. */
function periodoEmFrase(p: Periodo): string {
  return p.de === p.ate ? `em ${diaBr(p.de)}` : `de ${diaBr(p.de)} a ${diaBr(p.ate)}`;
}

/**
 * De onde vieram as avaliações do período. A frase existe porque o número do
 * app antigo e o do hub se liam como uma coisa só — e, com o hub ainda fechado
 * aos alunos, quase tudo é do app antigo: "nenhuma do hub" é informação, não
 * enfeite.
 */
function procedencia(l: LinhaDaFerramenta): string {
  if (l.avaliacoes === 0) return "";
  const umaSo = l.avaliacoes === 1;
  const todas = umaSo ? "A avaliação de aluno do período" : `As ${numero(l.avaliacoes)} avaliações de alunos do período`;
  const verbo = umaSo ? "veio" : "vieram";
  if (l.hub === 0) return `${todas} ${verbo} do app antigo (a Central Feedbacks), e nenhuma do hub.`;
  if (l.central_feedbacks === 0) return `${todas} ${verbo} do hub, e nenhuma do app antigo.`;
  return `Das ${numero(l.avaliacoes)} avaliações de alunos do período, ${numero(l.hub)} ${
    l.hub === 1 ? "veio" : "vieram"
  } do hub e ${numero(l.central_feedbacks)} do app antigo (a Central Feedbacks).`;
}

function daEquipe(n: number): string {
  if (n === 0) return "";
  return n === 1 ? "Uma avaliação da equipe fica fora da conta." : `${numero(n)} avaliações da equipe ficam fora da conta.`;
}

const TOM_DO_SENTIDO = { melhor: "text-aviso-tinta", pior: "text-erro-tinta", igual: "text-cinza", sem: "text-cinza" } as const;

/**
 * A média contra o período anterior, como o banco a calculou (em pontos, sobre
 * as duas médias arredondadas). O SINAL (▲, ▼ ou =, a peça da Visão geral) e o
 * número dizem para que lado foi; a cor só confirma — quem não distingue verde
 * e vermelho lê o mesmo.
 */
function VariacaoDaMedia({ linha }: { linha: LinhaDaFerramenta }) {
  // `anterior_avaliacoes` nulo é o período SEM anterior ("Todos"); zero é o
  // anterior sem avaliação. As duas frases são diferentes porque as duas
  // situações pedem leituras diferentes. (O hub ainda sem avaliação do período,
  // 000334, não chega aqui: sem avaliação, a variação nem é desenhada, e quem
  // diz por quê é a frase do período vazio.)
  const semAnterior = linha.anterior_avaliacoes === null;
  const v = linha.variacao_media;
  const sentido = semAnterior || v === null ? "sem" : sentidoDaVariacao(v, true);
  return (
    <dd className={`mt-0.5 text-xs leading-snug ${TOM_DO_SENTIDO[sentido]}`} data-avaliacao-variacao data-sentido={sentido}>
      {!semAnterior && v !== null && <SinalDaVariacao v={v} className="mr-1 inline-block" />}
      {semAnterior
        ? "sem período anterior para comparar"
        : v === null
          ? "sem avaliação no período anterior"
          : `${variacaoLegivel(v, "pontos")} contra o período anterior`}
    </dd>
  );
}

export default function AvaliacoesDaFerramenta({
  linha,
  pedido,
  importacao,
  comparacao,
}: {
  linha: LinhaDaFerramenta;
  pedido: PedidoDePeriodo;
  importacao: Importacao;
  /** Como o período da linha é comparado (000334), para o período vazio dizer por quê. */
  comparacao?: ComparacaoDoPeriodo;
}) {
  const idNumeros = useId();
  const idComentarios = useId();
  const chavePeriodo = chaveDoPedido(pedido);
  const periodoNaBusca = buscaSoDoPeriodo(pedido);

  // A PÁGINA pedida vale para a lista em que foi pedida (esta ferramenta, este
  // período): trocar o período volta à primeira página sem um segundo pedido —
  // a página 3 de um período não é a página 3 de outro. Zerar num efeito
  // pediria a página velha do período novo antes de pedir a primeira.
  const chaveDaLista = `${linha.slug}|${chavePeriodo}`;
  const [pagina, setPagina] = useState({ lista: chaveDaLista, indice: 0 });
  const indice = pagina.lista === chaveDaLista ? pagina.indice : 0;

  // Só vale a resposta do ÚLTIMO pedido, e a releitura mantém a lista na tela
  // (`useLeitura`): dois cliques seguidos em "Próximos" não desenham a página
  // errada, e a troca de período não pisca um carregador.
  const { estado, recarregar } = useLeitura(
    (sinal) =>
      lerAvaliacoes(
        pedido,
        {
          card: linha.slug,
          busca: "",
          mediaMin: null,
          mediaMax: null,
          comentario: true,
          ordem: "recentes",
          limite: POR_PAGINA,
          offset: indice * POR_PAGINA,
        },
        sinal,
      ),
    `${chaveDaLista}|${indice}`,
  );

  // A ÚLTIMA página que chegou, para quando a seguinte falhar: o erro de UMA
  // página fica ao lado da paginação, e a que está na tela continua — trocar a
  // lista inteira por "não veio" apagaria o que a pessoa estava lendo por causa
  // de um clique. Ela só serve à lista do MESMO período (a conferência é pelo
  // período que a resposta declara, e não pelo que a URL pede).
  const [reserva, setReserva] = useState<ListaDeAvaliacoes | null>(null);
  useEffect(() => {
    if (estado.fase === "pronto") setReserva(estado.dados);
  }, [estado]);
  const reservaValida = reserva && chaveDoPeriodo(reserva.periodo) === chavePeriodo ? reserva : null;

  const falhou = estado.fase === "erro" || estado.fase === "negado";
  const erro = falhou ? estado.detalhe : null;
  const dados = estado.fase === "pronto" ? estado.dados : reservaValida;
  const trocando = (estado.fase === "pronto" && estado.atualizando) || (estado.fase === "carregando" && reservaValida !== null);
  const fase: "carregando" | "pronto" | "erro" = dados ? "pronto" : falhou ? "erro" : "carregando";

  const limite = dados?.limite || POR_PAGINA;
  const indiceMostrado = dados ? Math.floor(dados.offset / limite) : 0;
  const total = dados?.total ?? 0;
  const itens = dados?.itens ?? [];
  const paginas = Math.max(1, Math.ceil(total / limite));
  const primeiro = indiceMostrado * limite + 1;
  const ultimo = indiceMostrado * limite + itens.length;

  // Rolar até o título quando a página NOVA chegar — e só ela. O alvo guarda
  // qual lista e qual página foi pedida: se o período mudar no meio, a resposta
  // é de outra lista e não se rola (trocar o período não é escolher página).
  const titulo = useRef<HTMLHeadingElement>(null);
  const pendente = useRef<{ lista: string; offset: number } | null>(null);
  useEffect(() => {
    const alvo = pendente.current;
    if (!alvo) return;
    if (estado.fase === "erro" || estado.fase === "negado") {
      pendente.current = null;
      return;
    }
    if (estado.fase !== "pronto" || estado.atualizando) return;
    if (alvo.lista !== chaveDaLista) {
      pendente.current = null;
      return;
    }
    // A resposta tem de ser a da página PEDIDA. Até 29/09 esta linha segurava o
    // quadro em que a leitura ainda não tinha marcado "atualizando" (ela o
    // marcava num efeito); desde que `useLeitura` deriva o estado no render,
    // esse quadro já chega aqui como "atualizando" e sai na linha de cima. A
    // conferência fica porque é ela que liga a rolagem à página que foi pedida
    // — e não à que estiver na tela.
    if (estado.dados.offset !== alvo.offset) return;
    pendente.current = null;
    const el = titulo.current;
    if (!el) return;
    // Quem clicou "Próximos" está no pé da lista, e a página nova começa lá em
    // cima: sem levar o olho até ela, a troca acontece fora da vista. E se o
    // foco caiu no corpo da página (o botão apertado ficou desligado, na última
    // página), ele vai para o título, que é onde a página nova começa — sem
    // isso, quem navega pelo teclado recomeça do alto da tela.
    const ativo = document.activeElement;
    if (!ativo || ativo === document.body) el.focus({ preventScroll: true });
    rolarAte(el);
  }, [estado, chaveDaLista]);

  function irPara(i: number) {
    // Enquanto uma página está a caminho, o clique seguinte esperaria a
    // anterior: sem esta guarda, dois cliques rápidos em "Próximos" pulariam a
    // página 2 sem a pessoa vê-la.
    if (trocando) return;
    pendente.current = { lista: chaveDaLista, offset: i * POR_PAGINA };
    // A mesma página de novo é a nova tentativa depois de uma falha: a chave da
    // leitura não muda, então quem pede de novo é o `recarregar`.
    if (i === indice) recarregar();
    else setPagina({ lista: chaveDaLista, indice: i });
  }

  const linkFeedbacks = `/admin/avaliacoes/feedbacks?card=${encodeURIComponent(linha.slug)}${periodoNaBusca ? `&${periodoNaBusca}` : ""}`;
  const nota = [procedencia(linha), daEquipe(linha.equipe)].filter(Boolean).join(" ");
  const fraseDaImportacaoDaPagina = fraseDaImportacao(importacao);

  return (
    <div
      className="space-y-5"
      data-avaliacoes-da-ferramenta={fase}
      data-periodo-das-avaliacoes={dados ? chaveDoPeriodo(dados.periodo) : undefined}
    >
      <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby={idNumeros} data-numeros-da-ferramenta>
        <h3 id={idNumeros} className="font-display text-lg font-bold text-titulo">
          Avaliações dos alunos
        </h3>

        {linha.avaliacoes === 0 ? (
          <p className="mt-3 text-sm text-cinza" data-sem-avaliacao>
            {comparacao === "sem_dado_ainda"
              ? AINDA_SEM_AVALIACAO
              : pedido.chave === "tudo"
                ? "Nenhuma avaliação de aluno ainda."
                : "Nenhuma avaliação de aluno no período."}
          </p>
        ) : (
          <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <div className="col-span-2 sm:col-span-1">
              <dt className="text-xs text-cinza">Média geral</dt>
              <dd className="font-display text-3xl font-bold tabular-nums text-titulo" data-avaliacao-media-geral>
                {notaLegivel(linha.media_geral)}
              </dd>
              <dd className="text-xs text-cinza">de 10</dd>
              <VariacaoDaMedia linha={linha} />
            </div>
            <div>
              <dt className="text-xs text-cinza">Utilidade</dt>
              <dd className="font-display text-2xl font-bold tabular-nums text-titulo" data-avaliacao-utilidade>
                {notaLegivel(linha.utilidade)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-cinza">Facilidade</dt>
              <dd className="font-display text-2xl font-bold tabular-nums text-titulo" data-avaliacao-facilidade>
                {notaLegivel(linha.facilidade)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-cinza">Satisfação</dt>
              <dd className="font-display text-2xl font-bold tabular-nums text-titulo" data-avaliacao-satisfacao>
                {notaLegivel(linha.satisfacao)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-cinza">Avaliações</dt>
              <dd className="font-display text-2xl font-bold tabular-nums text-titulo" data-avaliacao-total>
                {numero(linha.avaliacoes)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-cinza">Com comentário</dt>
              <dd className="font-display text-2xl font-bold tabular-nums text-titulo" data-avaliacao-com-comentario>
                {numero(linha.com_comentario)}
              </dd>
            </div>
          </dl>
        )}

        {/* A procedência de cada número, logo embaixo dele: sem ela, o número do
            app antigo e o do hub se liam como uma coisa só. */}
        <p className="mt-4 text-xs leading-relaxed text-cinza" data-avaliacao-nota>
          A média geral é a média, por avaliação, das três notas (utilidade, facilidade e satisfação) — a mesma conta do
          painel da Central Feedbacks. As notas vão de 0 a 10: o formulário do hub vai de 1 a 10, e o app antigo aceitava
          zero.{nota ? ` ${nota}` : ""}
        </p>
        {/* A frase é a da Visão geral (`fraseDaImportacao`, num lugar só). Sem o
            campo na resposta, ela não aparece: ausente é "não sei". */}
        {fraseDaImportacaoDaPagina && (
          <p className="mt-1 text-xs leading-relaxed text-cinza" data-avaliacao-importacao>
            {fraseDaImportacaoDaPagina}
          </p>
        )}
      </section>

      <EvolucaoDaFerramenta linha={linha} pedido={pedido} />

      <section
        className="surface rise p-6 sm:p-7"
        role="group"
        aria-labelledby={idComentarios}
        aria-busy={trocando}
        data-comentarios-da-ferramenta
      >
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
          {/* `tabIndex={-1}`: o título recebe o foco que cair no corpo da página
              depois de trocar de página (ver o efeito da rolagem). */}
          <h3
            id={idComentarios}
            ref={titulo}
            tabIndex={-1}
            className="font-display text-lg font-bold text-titulo"
            data-titulo-dos-comentarios
          >
            Comentários{" "}
            {dados && (
              <span className="font-sans text-sm font-normal text-cinza" data-comentarios-total={total}>
                ({numero(total)})
              </span>
            )}
          </h3>
          <Link to={linkFeedbacks} className="text-sm font-semibold text-link underline-offset-2 hover:underline" data-ver-no-feedbacks>
            Ver todas as avaliações desta ferramenta
          </Link>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-cinza">
          As avaliações de alunos do período que têm comentário, da mais recente para a mais antiga. As sem comentário
          estão nos Feedbacks, pelo link acima.
        </p>

        {fase === "carregando" ? (
          <p className="mt-4 text-sm text-cinza">Carregando os comentários…</p>
        ) : fase === "erro" ? (
          <div className="mt-4" role="alert">
            <p className="text-sm text-cinza">{fraseSolta(erro ?? "os comentários não vieram.")}</p>
            <button type="button" onClick={recarregar} className="btn-contorno mt-3 min-h-10 text-sm" data-tentar-de-novo>
              Tentar de novo
            </button>
          </div>
        ) : itens.length === 0 ? (
          <p className="mt-4 text-sm text-cinza" data-sem-comentario>
            {total > 0
              ? "Esta página ficou vazia: a lista encolheu desde que foi aberta (um aluno pode ter mudado a avaliação). Volte à anterior."
              : comparacao === "sem_dado_ainda"
                ? AINDA_SEM_AVALIACAO
                : pedido.chave === "tudo"
                  ? "Nenhuma avaliação de aluno com comentário ainda."
                  : "Nenhuma avaliação de aluno com comentário no período."}
          </p>
        ) : (
          // A lista DECLARA de que página são os comentários que ela mostra (o
          // que o banco devolveu, e não o botão que foi clicado): entre o clique
          // e a resposta, a página anterior continua de pé, e quem espera "a
          // lista mudou" sem olhar a página lê a antiga.
          <ul
            className={`mt-4 grid gap-2.5 transition-opacity ${trocando ? "opacity-60" : ""}`}
            data-lista-de-comentarios
            data-pagina-dos-comentarios={indiceMostrado + 1}
          >
            {itens.map((c) => {
              const q = quandoLegivel(c.em);
              return (
                <li key={c.seq} className="rounded-xl border border-borda p-4" data-comentario-item data-avaliacao={c.seq}>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-cinza">
                    <span data-comentario-media={c.media}>
                      <SeloDaMedia media={c.media} />
                    </span>
                    <span className="tabular-nums">
                      utilidade {c.utilidade} · facilidade {c.facilidade} · satisfação {c.satisfacao}
                    </span>
                    <span className="tabular-nums">
                      {q.dia}, {horaComArtigo(q.hora)}
                    </span>
                    <span data-comentario-origem={c.origem}>no {ROTULO_DA_ORIGEM[c.origem]}</span>
                  </div>
                  {/* Texto do aluno: o React escapa, e a quebra de linha dele fica. */}
                  <p className="mt-2 text-sm leading-relaxed whitespace-pre-line text-tinta" data-comentario-texto>
                    {c.comentario}
                  </p>
                </li>
              );
            })}
          </ul>
        )}

        {/* A paginação DIZ o total e onde se está: "21–40 de 81" responde
            "quantos faltam" sem conta de cabeça. Some quando tudo cabe numa
            página. O `disabled` é só o da PONTA (a primeira e a última página);
            enquanto a página nova vem, o botão fica focável e o clique espera
            (`aria-disabled`) — desligar o botão apertado tiraria o foco de quem
            navega pelo teclado. */}
        {dados && total > limite && (
          <nav className="mt-4 flex flex-wrap items-center justify-between gap-3" aria-label="Páginas dos comentários" data-paginacao-comentarios>
            <button
              type="button"
              data-pagina-anterior
              disabled={indiceMostrado === 0}
              aria-disabled={trocando || undefined}
              onClick={() => irPara(indiceMostrado - 1)}
              className="btn-contorno text-sm disabled:cursor-not-allowed disabled:opacity-45 aria-disabled:opacity-60"
            >
              ← Anteriores
            </button>
            <span className="text-xs tabular-nums text-cinza" role="status" data-pagina-atual={indiceMostrado + 1} data-paginas={paginas}>
              {itens.length > 0 ? `${numero(primeiro)}–${numero(ultimo)} de ${numero(total)} · ` : ""}
              página {indiceMostrado + 1} de {paginas}
            </span>
            <button
              type="button"
              data-pagina-proxima
              disabled={ultimo >= total}
              aria-disabled={trocando || undefined}
              onClick={() => irPara(indiceMostrado + 1)}
              className="btn-contorno text-sm disabled:cursor-not-allowed disabled:opacity-45 aria-disabled:opacity-60"
            >
              Próximos →
            </button>
          </nav>
        )}
        {erro && dados && (
          <p className="mt-2 text-xs text-cinza" role="alert" data-erro-da-pagina>
            {fraseSolta(erro)}{" "}
            <button
              type="button"
              onClick={() => irPara(indice)}
              className="font-semibold text-link underline underline-offset-2"
              data-tentar-de-novo
            >
              Tentar de novo
            </button>
          </p>
        )}
      </section>
    </div>
  );
}

// ── A evolução ──────────────────────────────────────────────────────────────

/**
 * As quatro notas no tempo, pelos baldes do período — o "Evolução semanal" do
 * painel antigo, que lá era sempre por semana e sempre de todo o tempo. Aqui o
 * recorte é o do período, e quem o escolhe é o banco pelo tamanho dele (30 dias
 * saem por dia; 90, por semana; mais de 190 dias, por mês — "Todos" é por
 * semana enquanto o acervo tiver menos de 191 dias: medido em 29/09, eram 142,
 * de 11/05 a 29/09). O ponto
 * sustentado por menos de `POUCAS` avaliações sai vazado: uma média de três
 * avaliações parece uma queda e é uma pessoa.
 *
 * A leitura é própria (a série é outra pergunta ao banco), e a META tracejada
 * sai da `linha`, e não da resposta da série: depois de mudar a meta a página
 * relê a linha, e a série — que não depende da meta — não é pedida de novo;
 * com a meta da série, a linha tracejada ficaria na meta antiga ao lado do
 * bloco que já mostra a nova.
 */
function EvolucaoDaFerramenta({ linha, pedido }: { linha: LinhaDaFerramenta; pedido: PedidoDePeriodo }) {
  const idTitulo = useId();
  const { estado, recarregar } = useLeitura((sinal) => lerSeries([linha.slug], pedido, sinal), `${linha.slug}|${chaveDoPedido(pedido)}`);
  const dados = estado.fase === "pronto" ? estado.dados : null;
  const atualizando = estado.fase === "pronto" && estado.atualizando;
  const meta = linha.meta;

  let conteudo: ReactNode = null;
  if (estado.fase === "carregando") {
    conteudo = <p className="flex min-h-64 items-center justify-center text-sm text-cinza">Carregando a evolução…</p>;
  } else if (estado.fase === "erro" || estado.fase === "negado") {
    conteudo = (
      <div className="mt-4" role="alert">
        <p className="text-sm text-cinza">{fraseSolta(estado.detalhe)}</p>
        <button type="button" onClick={recarregar} className="btn-contorno mt-3 min-h-10 text-sm" data-tentar-de-novo>
          Tentar de novo
        </button>
      </div>
    );
  } else if (dados) {
    const g = dados.periodo.granularidade;
    const serie = dados.series.find((s) => s.card === linha.slug) ?? dados.series[0] ?? null;
    // Alinhado pela CHAVE do balde, e não pela posição: é o balde que diz onde
    // o ponto mora no eixo, e um ponto fora de ordem não pode mudar de dia.
    const porChave = new Map((serie?.pontos ?? []).map((pt) => [pt.chave, pt]));
    const contagens = dados.baldes.map((b) => porChave.get(b.chave)?.avaliacoes ?? 0);
    const series: SerieDoGrafico[] = serie
      ? NOTAS_DA_EVOLUCAO.map((n) => ({
          id: n.id,
          rotulo: n.rotulo,
          p: n.p,
          valores: dados.baldes.map((b) => porChave.get(b.chave)?.[n.id] ?? null),
          contagens,
        }))
      : [];
    const quando = periodoEmFrase(dados.periodo);
    conteudo = (
      <>
        {/* A nota do ponto vazado é do próprio gráfico, e só aparece quando há
            um: dizê-la aqui também a repetiria em todo período. */}
        <p className="mt-1 text-xs leading-relaxed text-cinza" data-frase-da-evolucao>
          {comMaiuscula(POR_GRANULARIDADE[g])}, {quando}, no fuso de São Paulo. Cada ponto é a média das
          avaliações de alunos {DE_CADA_PONTO[g]}.
          {meta !== null ? " A linha tracejada é a meta." : " A ferramenta não tem meta."}
        </p>
        <div className="mt-4">
          <GraficoDeLinhas
            baldes={dados.baldes}
            granularidade={g}
            series={series}
            metas={meta === null ? [] : [{ valor: meta, rotulo: `meta ${metaLegivel(meta)}` }]}
            descricao={`Evolução das notas de ${linha.nome}, ${POR_GRANULARIDADE[g]}, ${quando}: a média geral e as notas de utilidade, facilidade e satisfação${
              meta !== null ? `, com a meta de ${metaLegivel(meta)}` : ""
            }.`}
            semPontos={
              dados.periodo.comparacao === "sem_dado_ainda"
                ? AINDA_SEM_AVALIACAO
                : dados.periodo.chave === "tudo"
                ? "Nenhuma avaliação de aluno ainda — não há evolução a mostrar."
                : "Nenhuma avaliação de aluno no período — não há evolução a mostrar."
            }
            rotuloDaTabela={`As notas ${POR_GRANULARIDADE[g]}, com quantas avaliações sustentam cada ponto`}
            dado="evolucao-da-ferramenta"
            horizonte={dados.periodo.horizonte}
          />
        </div>
      </>
    );
  }

  return (
    <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby={idTitulo} data-evolucao-da-ferramenta>
      <h3 id={idTitulo} className="font-display text-lg font-bold text-titulo">
        Evolução no período
      </h3>
      <div
        className={`transition-opacity ${atualizando ? "opacity-60" : ""}`}
        aria-busy={estado.fase === "carregando" || atualizando}
        data-grafico-pronto={estado.fase === "pronto" && !atualizando}
        data-periodo-do-grafico={dados ? chaveDoPeriodo(dados.periodo) : undefined}
      >
        {conteudo}
      </div>
    </section>
  );
}
